// TODO: refactor

import {
  PopoverPortal as UIKitPopoverPortal,
  PopoverPortalContext,
} from "@circle-react-shared/uikit/PopoverPortal";
import { NotificationsList } from "@circle-react/components/Notifications/NotificationsList";
import {
  bookmarksPath,
  chatRoomsPath,
  chatThreadsPath,
  connectionsFullPagePath,
  getRouteWithPrevLocation,
  notificationsFullPagePath,
  notificationsPath,
} from "@circle-react/helpers/urlHelpers";
import { Popover } from "@circleco/compass/components/Popover";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { isFunction } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import {
  useLargeScreenMediaQuery,
  useTailwindLgScreenMediaQuery,
  useTailwindMdScreenMediaQuery,
} from "@/react/hooks/useMediaQuery";

import { PlatformMenuItem } from "./PlatformMenuItem";

interface MemberLinks {
  allDMsBadgeText?: string;
  connectBadgeText?: string;
  disabled?: boolean;
  isMessagingEnabled?: boolean;
  notificationBadgeText?: string;
  onNotificationClick?: () => void;
  resetNewNotificationsCount?: () => void;
  shouldShowChatThreadsTab?: boolean;
  unreadThreadsCount?: number;
}

export const MemberLinks = ({
  allDMsBadgeText,
  disabled,
  isMessagingEnabled,
  notificationBadgeText,
  onNotificationClick,
  resetNewNotificationsCount,
  shouldShowChatThreadsTab,
  unreadThreadsCount,
  connectBadgeText,
}: MemberLinks) => {
  const navigate = useNavigate();
  const pathname = useLocation({ select: location => location.pathname });
  const isLgScreen = useLargeScreenMediaQuery();
  const isLgScreenTw = useTailwindLgScreenMediaQuery();
  const isMdScreen = useTailwindMdScreenMediaQuery();
  const isConnectEnabled = useIsConnectEnabled();
  const isCompassEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_popover_enabled",
  );

  const isMessagingActive = pathname.includes(chatRoomsPath());
  const isChatThreadsActive = pathname.includes(chatThreadsPath());
  const isNotificationsActive = pathname.includes(notificationsPath());
  const isConnectionsActive = pathname.includes(connectionsFullPagePath());
  const isBookmarksActive = pathname.includes(bookmarksPath());

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const location = useLocation();
  const notificationsContextValue = useMemo(
    () => ({ onClose: () => setIsNotificationsOpen(false) }),
    [],
  );
  useEffect(() => {
    setIsNotificationsOpen(false);
  }, [location]);

  const renderNotificationsMenu = () => {
    if (isMdScreen) {
      return (
        <PlatformMenuItem
          dataTestId="notifications-menu"
          label={t("notifications.page_title")}
          iconName="sidebar-bell"
          badgeText={notificationBadgeText}
          ariaLabel={t("community_sidebar.unread_notifications", {
            count: Number(notificationBadgeText),
          })}
          onClick={() => {
            resetNewNotificationsCount?.();
            void navigate({ to: notificationsFullPagePath() });
          }}
          isActive={isNotificationsActive}
        />
      );
    }

    if (!isCompassEnabled) {
      return (
        <UIKitPopoverPortal
          button={
            <PlatformMenuItem
              dataTestId="notifications-menu"
              label={t("notifications.page_title")}
              iconName="sidebar-bell"
              badgeText={notificationBadgeText}
              ariaLabel={t("community_sidebar.unread_notifications", {
                count: Number(notificationBadgeText),
              })}
              onClick={() => {
                if (
                  isLgScreen &&
                  isFunction(onNotificationClick) &&
                  !disabled
                ) {
                  onNotificationClick();
                }
              }}
              isActive={isNotificationsActive}
            />
          }
          onOpen={resetNewNotificationsCount}
          disabled={disabled || !isFunction(onNotificationClick)}
        >
          <div
            className="flex size-full flex-col"
            data-testid="notifications-drawer"
          >
            <NotificationsList />
          </div>
        </UIKitPopoverPortal>
      );
    }

    return (
      <Popover
        trigger={
          // TODO: Replace with Compass component
          <PlatformMenuItem
            dataTestId="notifications-menu"
            label={t("notifications.page_title")}
            iconName="sidebar-bell"
            badgeText={notificationBadgeText}
            ariaLabel={t("community_sidebar.unread_notifications", {
              count: Number(notificationBadgeText),
            })}
            onClick={() => {
              if (isLgScreen && isFunction(onNotificationClick) && !disabled) {
                onNotificationClick();
              }
            }}
            isActive={isNotificationsActive}
          />
        }
        open={isNotificationsOpen}
        onOpenChange={(open: boolean) => {
          setIsNotificationsOpen(open);
          if (open) {
            resetNewNotificationsCount?.();
          }
        }}
        disabled={disabled || !isFunction(onNotificationClick)}
        side="bottom"
        align="start"
      >
        <PopoverPortalContext.Provider value={notificationsContextValue}>
          <div
            className="flex size-full flex-col"
            data-testid="notifications-drawer"
          >
            <NotificationsList />
          </div>
        </PopoverPortalContext.Provider>
      </Popover>
    );
  };

  return (
    <>
      {!isLgScreenTw && renderNotificationsMenu()}
      {isMessagingEnabled && !isLgScreenTw && (
        <PlatformMenuItem
          dataTestId="chat-menu"
          label={t("sidebar_all_dms")}
          iconName="16-messages"
          badgeText={allDMsBadgeText}
          ariaLabel={t("community_sidebar.unread_messages", {
            count: Number(allDMsBadgeText),
          })}
          onClick={() => {
            const { pathname, state } = getRouteWithPrevLocation({
              url: chatRoomsPath(),
            });
            void navigate({
              to: pathname,
              state: state as Record<string, unknown>,
            });
          }}
          isActive={isMessagingActive}
        />
      )}
      {shouldShowChatThreadsTab && !isLgScreenTw && (
        <PlatformMenuItem
          dataTestId="chat-threads-menu"
          label={t("chat_threads")}
          iconName="16-chat-threads"
          onClick={() => void navigate({ to: chatThreadsPath() })}
          isActive={isChatThreadsActive}
          badgeText={String(unreadThreadsCount)}
          ariaLabel={t("community_sidebar.unread_messages", {
            count: Number(unreadThreadsCount),
          })}
        />
      )}
      {isConnectEnabled && !isLgScreenTw && (
        <PlatformMenuItem
          dataTestId="connections-menu"
          label={t("header_v3.connect.title")}
          iconName="20-connections"
          onClick={() => void navigate({ to: connectionsFullPagePath() })}
          isActive={isConnectionsActive}
          badgeText={connectBadgeText}
          ariaLabel={t("community_sidebar.new_connections", {
            count: Number(connectBadgeText),
          })}
        />
      )}
      {isLgScreen && (
        <PlatformMenuItem
          dataTestId="bookmarks-menu"
          label={t("header_v3.bookmarks")}
          iconName="16-bookmark"
          onClick={() => void navigate({ to: bookmarksPath() })}
          isActive={isBookmarksActive}
        />
      )}
    </>
  );
};
